import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useReports } from '../hooks/useReports';
import { useFirebase } from '../lib/firebase/FirebaseProvider';
import ReportUpdates from '../components/ReportUpdates';
import Breadcrumb from '../components/Breadcrumb';
import { FeatureGate } from '../components/FeatureGate';
import { ClickableLocation } from '../components/ClickableLocation';
import ShareModal from '../components/ShareModal';
import { SidebarAd } from '../components/ads/SidebarAd';

interface Report {
  id: string;
  title: string;
  description: string;
  status: string;
  location: {
    latitude: number;
    longitude: number;
    address: string;
    suburb: string;
    council: string;
    postcode: string;
  };
  images: Array<{
    url: string;
    altText?: string;
    orderIndex: number;
  }>;
  tags: string[];
  categories?: Array<{
    id: string;
    name: string;
    color?: string;
  }>;
  submittedBy: string;
  submittedAt: any;
  approvedAt?: any;
  pickupDate?: any;
  viewCount: number;
}

const ReportDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useFirebase();
  const { fetchReport, updateReportStatus } = useReports();
  
  const [report, setReport] = useState<Report | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [showLightbox, setShowLightbox] = useState(false);
  const [isFavorited, setIsFavorited] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);

  useEffect(() => {
    if (!id) {
      navigate('/treasures');
      return;
    }

    const loadReport = async () => {
      setIsLoading(true);
      setError(null);
      
      try {
        const reportData = await fetchReport(id);
        if (reportData) {
          setReport(reportData);
        } else {
          setError('Report not found');
        }
      } catch (err) {
        console.error('Error loading report:', err);
        setError('Failed to load report');
      } finally {
        setIsLoading(false);
      }
    };

    loadReport();
  }, [id, fetchReport, navigate]);

  const formatDate = (timestamp: any) => {
    if (!timestamp) return 'N/A';
    
    let date;
    try {
      if (timestamp.toDate && typeof timestamp.toDate === 'function') {
        date = timestamp.toDate();
      } else if (timestamp instanceof Date) {
        date = timestamp;
      } else if (typeof timestamp === 'string') {
        date = new Date(timestamp);
      } else if (typeof timestamp === 'number') {
        date = new Date(timestamp);
      } else {
        console.warn('Unknown timestamp format:', timestamp);
        return 'Invalid Date';
      }
      
      // Check if the date is valid
      if (isNaN(date.getTime())) {
        console.warn('Invalid date created from timestamp:', timestamp);
        return 'Invalid Date';
      }
      
      return date.toLocaleDateString();
    } catch (error) {
      console.error('Error formatting date:', error, timestamp);
      return 'Invalid Date';
    }
  };

  const formatDateTime = (timestamp: any) => {
    if (!timestamp) return 'N/A';
    
    let date;
    try {
      if (timestamp.toDate && typeof timestamp.toDate === 'function') {
        date = timestamp.toDate();
      } else if (timestamp instanceof Date) {
        date = timestamp;
      } else if (typeof timestamp === 'string') {
        date = new Date(timestamp);
      } else if (typeof timestamp === 'number') {
        date = new Date(timestamp);
      } else {
        console.warn('Unknown timestamp format:', timestamp);
        return 'Invalid Date';
      }
      
      // Check if the date is valid
      if (isNaN(date.getTime())) {
        console.warn('Invalid date created from timestamp:', timestamp);
        return 'Invalid Date';
      }
      
      return date.toLocaleDateString() + ' at ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } catch (error) {
      console.error('Error formatting datetime:', error, timestamp);
      return 'Invalid Date';
    }
  };

  const getStatusConfig = (status: string) => {
    switch (status) {
      case 'active':
        return {
          label: 'Available',
          className: 'bg-verge text-white',
          icon: '✓'
        };
      case 'going_soon':
        return {
          label: 'Going Soon',
          className: 'bg-treasure text-urban',
          icon: '⏰'
        };
      case 'gone':
        return {
          label: 'Gone',
          className: 'bg-urban text-white',
          icon: '✗'
        };
      case 'in_review':
        return {
          label: 'In Review',
          className: 'bg-cardboard text-white',
          icon: '👁'
        };
      case 'rejected':
        return {
          label: 'Rejected',
          className: 'bg-rust text-white',
          icon: '✗'
        };
      default:
        return {
          label: status.replace('_', ' ').toUpperCase(),
          className: 'bg-urban-200 text-urban',
          icon: '?'
        };
    }
  };

  const canUpdateStatus = (currentStatus: string) => {
    if (!user || !report) return false;
    
    // Users can mark their own reports as gone
    if (user.uid === report.submittedBy) {
      return ['active', 'going_soon'].includes(currentStatus);
    }
    
    // TODO: Add admin check when admin functionality is implemented
    return false;
  };

  const handleStatusUpdate = async (newStatus: string) => {
    if (!report || !id) return;
    
    setIsUpdatingStatus(true);
    try {
      await updateReportStatus(id, newStatus as "in_review" | "active" | "going_soon" | "gone" | "rejected");
      setReport(prev => prev ? { ...prev, status: newStatus } : null);
    } catch (err) {
      console.error('Error updating status:', err);
      setError('Failed to update report status');
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const handleImageNavigation = (direction: 'prev' | 'next') => {
    if (!report?.images) return;
    
    if (direction === 'prev') {
      setSelectedImageIndex(prev => 
        prev === 0 ? report.images.length - 1 : prev - 1
      );
    } else {
      setSelectedImageIndex(prev => 
        prev === report.images.length - 1 ? 0 : prev + 1
      );
    }
  };

  const handleFavoriteToggle = () => {
    setIsFavorited(!isFavorited);
    // TODO: Implement favorite functionality with backend
  };

  const handleShareClick = () => {
    setShowShareModal(true);
  };

  const handleShareModalClose = () => {
    setShowShareModal(false);
  };

  const openLightbox = (index: number) => {
    setSelectedImageIndex(index);
    setShowLightbox(true);
  };

  const closeLightbox = () => {
    setShowLightbox(false);
  };

  const handleLightboxNavigation = (direction: 'prev' | 'next', e: React.MouseEvent) => {
    e.stopPropagation();
    handleImageNavigation(direction);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-sky-neutral">
        <div className="max-w-7xl mx-auto px-6 py-8">
          {/* Loading skeleton */}
          <div className="animate-pulse">
            <div className="h-4 bg-teddy rounded w-32 mb-6"></div>
            <div className="h-8 bg-teddy rounded w-96 mb-4"></div>
            <div className="h-4 bg-teddy rounded w-64 mb-8"></div>
            <div className="h-96 bg-teddy rounded-2xl mb-8"></div>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <div className="lg:col-span-2">
                <div className="h-64 bg-teddy rounded-2xl"></div>
              </div>
              <div className="h-96 bg-teddy rounded-2xl"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !report) {
    return (
      <div className="min-h-screen bg-sky-neutral">
        <div className="max-w-7xl mx-auto px-6 py-8">
          <div className="text-center py-20">
            <div className="text-rust mb-6">
              <svg className="w-20 h-20 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h2 className="text-3xl font-bold text-urban mb-4">Treasure Not Found</h2>
            <p className="text-urban-400 mb-8 max-w-md mx-auto">{error || 'The treasure you\'re looking for doesn\'t exist or has been removed from our quest.'}</p>
            <Link
              to="/treasures"
              className="inline-flex items-center gap-2 px-6 py-3 bg-cardboard text-white rounded-xl hover:bg-cardboard-600 transition-all duration-200 hover:shadow-lg"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              Back to Treasure Hunt
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const statusConfig = getStatusConfig(report.status);

  return (
    <div className="min-h-screen bg-sky-neutral">
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Breadcrumb Navigation */}
        <Breadcrumb 
          items={[
            { label: 'Treasures', path: '/treasures' },
            { label: report.location.suburb, path: `/treasures?suburb=${report.location.suburb}` },
            { label: 'Treasure Details' }
          ]} 
        />

        {/* Header Section */}
        <div className="mb-8">
          <div className="flex items-start justify-between mb-4">
            <div className="flex-1">
              {/* Title removed - focusing on visual content and location */}
              <div className="flex items-center gap-4 text-urban-400">
                <ClickableLocation
                  location={{
                    latitude: report.location.latitude,
                    longitude: report.location.longitude,
                    address: report.location.address,
                    suburb: report.location.suburb,
                    postcode: report.location.postcode
                  }}
                  displayText={`${report.location.suburb}, ${report.location.postcode}`}
                  className="text-urban-400 hover:text-cardboard"
                  showIcon={true}
                />
                <div className="flex items-center gap-1">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                    <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
                  </svg>
                  <span>{report.viewCount || 0} views</span>
                </div>
                <div className="flex items-center gap-1">
                  <span className={`inline-flex items-center gap-1 px-3 py-1 text-sm font-semibold rounded-full ${statusConfig.className}`}>
                    <span>{statusConfig.icon}</span>
                    {statusConfig.label}
                  </span>
                </div>
              </div>
            </div>
            
            {/* Action Buttons */}
            <div className="flex items-center gap-3 ml-6">
              <button
                onClick={handleFavoriteToggle}
                className={`p-3 rounded-full transition-all duration-200 hover:scale-110 ${
                  isFavorited 
                    ? 'bg-rust text-white shadow-lg' 
                    : 'bg-white hover:bg-rust-50 text-rust border border-rust-200'
                }`}
                aria-label={isFavorited ? 'Remove from favorites' : 'Add to favorites'}
              >
                <svg className="w-5 h-5" fill={isFavorited ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                </svg>
              </button>
              
              <button 
                onClick={handleShareClick}
                className="p-3 rounded-full bg-white hover:bg-urban-50 text-urban border border-urban-200 transition-all duration-200 hover:scale-110"
                aria-label="Share this treasure"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z" />
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* Image Gallery */}
        {report.images && report.images.length > 0 && (
          <div className="mb-8">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-4 sm:gap-6">
              {/* Main Image */}
              <div 
                className="col-span-2 row-span-2 cursor-pointer group relative overflow-hidden"
                onClick={() => openLightbox(selectedImageIndex)}
              >
                <img
                  src={report.images[selectedImageIndex].url}
                  alt={report.images[selectedImageIndex].altText || 'Curbside treasure item'}
                  className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-10 transition-all duration-300 flex items-center justify-center">
                  <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-white rounded-full p-2">
                    <svg className="w-6 h-6 text-urban" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                    </svg>
                  </div>
                </div>
              </div>
              
              {/* Thumbnail Grid */}
              {report.images.slice(1, 5).map((image, index) => (
                <div 
                  key={index + 1}
                  className="cursor-pointer group relative overflow-hidden"
                  onClick={() => openLightbox(index + 1)}
                >
                  <img
                    src={image.url}
                    alt={image.altText || `Photo ${index + 2}`}
                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                  />
                  {index === 3 && report.images.length > 5 && (
                    <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
                      <span className="text-white font-semibold">+{report.images.length - 5} more</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
            
            {/* Show All Photos Button */}
            {report.images.length > 1 && (
              <button
                onClick={() => openLightbox(0)}
                className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-white border border-urban-200 rounded-xl hover:bg-urban-50 transition-colors duration-200"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                Show all {report.images.length} photos
              </button>
            )}
          </div>
        )}

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Description and Updates */}
          <div className="lg:col-span-2 space-y-8">
            {/* Description Section */}
            <div className="bg-white rounded-2xl p-8 shadow-card">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-cardboard rounded-full flex items-center justify-center">
                  <span className="text-white font-semibold">
                    {report.submittedBy.slice(0, 2).toUpperCase()}
                  </span>
                </div>
                <div>
                  <h3 className="font-semibold text-urban">Treasure Planter</h3>
                  <p className="text-sm text-urban-400">Submitted {formatDate(report.submittedAt)}</p>
                </div>
              </div>
              
              {/* Description removed - focusing on visual content and tags */}

              {/* Categories and Tags */}
              <div className="mt-6 pt-6 border-t border-urban-100 space-y-4">
                {/* Categories - displayed with distinct styling */}
                {report.categories && report.categories.length > 0 && (
                  <div>
                    <h4 className="font-semibold text-urban mb-3">Categories</h4>
                    <div className="flex flex-wrap gap-2">
                      {report.categories.map((category, index) => (
                        <span
                          key={`category-${index}`}
                          className="inline-flex px-3 py-1 text-sm bg-cardboard-100 text-cardboard-800 rounded-lg font-semibold border border-cardboard-200 hover:bg-cardboard-200 transition-colors cursor-pointer"
                          style={category.color ? { backgroundColor: `${category.color}20`, borderColor: `${category.color}40`, color: category.color } : {}}
                        >
                          {typeof category === 'string' ? category : category.name}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Tags - displayed with original styling */}
                {report.tags && report.tags.length > 0 && (
                  <div>
                    <h4 className="font-semibold text-urban mb-3">Tags</h4>
                    <div className="flex flex-wrap gap-2">
                      {report.tags.map((tag, index) => (
                        <span
                          key={`tag-${index}`}
                          className="inline-flex px-3 py-1 text-sm bg-verge-100 text-verge-800 rounded-xl font-medium hover:bg-verge-200 transition-colors cursor-pointer"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Updates Timeline */}
            <FeatureGate flag="ACTION_COMPONENTS_ENABLED">
              <ReportUpdates reportId={report.id} />
            </FeatureGate>
          </div>

          {/* Right Column - Action Sidebar */}
          <div className="space-y-6">
            {/* Location Details */}
            <div className="bg-white rounded-2xl p-6 shadow-card">
              <h3 className="font-semibold text-urban mb-4">Location Details</h3>
              <div className="space-y-3 text-sm">
                <div className="flex items-start gap-3">
                  <ClickableLocation
                    location={{
                      latitude: report.location.latitude,
                      longitude: report.location.longitude,
                      address: report.location.address,
                      suburb: report.location.suburb,
                      postcode: report.location.postcode
                    }}
                    className="text-urban hover:text-cardboard font-medium"
                    showIcon={true}
                  >
                    <div className="flex items-start gap-3">
                      <svg className="w-4 h-4 text-urban-400 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                      </svg>
                      <div>
                        <p className="font-medium text-urban">{report.location.address}</p>
                        <p className="text-urban-400">{report.location.suburb}, {report.location.postcode}</p>
                      </div>
                    </div>
                  </ClickableLocation>
                </div>

                {report.pickupDate && (
                  <div className="flex items-center gap-3">
                    <svg className="w-4 h-4 text-urban-400" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                    </svg>
                    <span className="text-urban-400">Pickup: {formatDate(report.pickupDate)}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Action Card */}
            <FeatureGate flag="ACTION_COMPONENTS_ENABLED">
              <div className="bg-white rounded-2xl p-6 shadow-card sticky top-6">
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-2xl font-bold text-urban">Free Treasure!</span>
                    <div className="flex items-center gap-1 text-treasure">
                      <span className="text-2xl">🪙</span>
                      <span className="font-bold">+5</span>
                    </div>
                  </div>
                  <p className="text-sm text-urban-400 mb-6">
                    Help the community by providing updates about this treasure
                  </p>
                </div>

                {/* Action Buttons */}
                <div className="space-y-3">
                  {canUpdateStatus(report.status) && (
                    <button
                      onClick={() => handleStatusUpdate('gone')}
                      disabled={isUpdatingStatus}
                      className="w-full py-3 bg-rust text-white rounded-xl font-semibold hover:bg-rust-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                    >
                      {isUpdatingStatus ? 'Updating...' : 'Mark as Gone'}
                    </button>
                  )}
                  
                  <button className="w-full py-3 bg-cardboard text-white rounded-xl font-semibold hover:bg-cardboard-600 transition-colors duration-200">
                    Add Update
                  </button>
                  
                  <button className="w-full py-3 bg-white border border-urban-200 text-urban rounded-xl font-semibold hover:bg-urban-50 transition-colors duration-200">
                    Report Issue
                  </button>
                </div>
              </div>
            </FeatureGate>

            {/* Sidebar Advertisement */}
            <FeatureGate flag="GOOGLE_ADS_ENABLED">
              <SidebarAd sticky={true} className="mb-6" />
            </FeatureGate>

            {/* Report Metadata */}
            <div className="bg-white rounded-2xl p-6 shadow-card">
              <h3 className="font-semibold text-urban mb-4">Report Information</h3>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-urban-400">Submitted</span>
                  <span className="text-urban">{formatDateTime(report.submittedAt)}</span>
                </div>
                {report.approvedAt && (
                  <div className="flex justify-between">
                    <span className="text-urban-400">Approved</span>
                    <span className="text-urban">{formatDateTime(report.approvedAt)}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-urban-400">Report ID</span>
                  <span className="text-urban font-mono text-xs">{report.id.slice(0, 8)}...</span>
                </div>
                {user && user.uid === report.submittedBy && (
                  <div className="mt-4 p-3 bg-cardboard-50 border border-cardboard-200 rounded-lg">
                    <div className="flex items-center gap-2">
                      <svg className="w-4 h-4 text-cardboard" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                      </svg>
                      <span className="text-cardboard-800 font-medium text-sm">Your Report</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Image Lightbox */}
        {showLightbox && report.images && (
          <div 
            className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center p-4"
            onClick={closeLightbox}
          >
            <div className="relative max-w-4xl max-h-full">
              <img
                src={report.images[selectedImageIndex].url}
                alt={report.images[selectedImageIndex].altText || 'Curbside treasure item'}
                className="max-w-full max-h-full object-contain"
                onClick={(e) => e.stopPropagation()}
              />
              
              {/* Close Button */}
              <button
                onClick={closeLightbox}
                className="absolute top-4 right-4 text-white bg-black bg-opacity-50 rounded-full p-2 hover:bg-opacity-75 transition-all duration-200"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
              
              {/* Navigation Arrows */}
              {report.images.length > 1 && (
                <>
                  <button
                    onClick={(e) => handleLightboxNavigation('prev', e)}
                    className="absolute left-4 top-1/2 transform -translate-y-1/2 text-white bg-black bg-opacity-50 rounded-full p-3 hover:bg-opacity-75 transition-all duration-200"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                  <button
                    onClick={(e) => handleLightboxNavigation('next', e)}
                    className="absolute right-4 top-1/2 transform -translate-y-1/2 text-white bg-black bg-opacity-50 rounded-full p-3 hover:bg-opacity-75 transition-all duration-200"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </>
              )}
              
              {/* Image Counter */}
              <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 text-white bg-black bg-opacity-50 px-4 py-2 rounded-full">
                {selectedImageIndex + 1} / {report.images.length}
              </div>
            </div>
          </div>
        )}

        {/* Error Display */}
        {error && (
          <div className="mt-8 bg-rust-50 border border-rust-200 rounded-2xl p-6">
            <div className="flex items-center gap-3">
              <svg className="w-5 h-5 text-rust flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <p className="text-rust-800">{error}</p>
            </div>
          </div>
        )}

        {/* Share Modal */}
        <ShareModal
          isOpen={showShareModal}
          onClose={handleShareModalClose}
          reportId={report.id}
          reportTitle={`Treasure found in ${report.location.suburb}`}
          reportImage={report.images?.[0]?.url}
          reportLocation={`${report.location.suburb}, ${report.location.postcode}`}
        />
      </div>
    </div>
  );
};

export default ReportDetailPage;